/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { SalesLine } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ReturnOrderProduct } from '../../order-return-initiate-product.component';

import React from 'react';

export interface IOrderReturnInitiateTableRowData {
    salesLine: SalesLine;
    returnProduct: ReturnOrderProduct;
}

export interface IOrderReturnInitiateTableRowResources {
    productSKULabel: string;
}

export interface IOrderReturnInitiateTableRowProps extends IComponentProps<IOrderReturnInitiateTableRowData> {
    context: ICoreContext;
    className: string;

    imageSettings?: IImageSettings;
    resources: IOrderReturnInitiateTableRowResources;
    onChange(): void;
}

const defaultImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=50&h=50&m=6', w: 0, h: 0 },
        lg: { q: 'w=80&h=80&m=6', w: 0, h: 0 }
    },
    lazyload: true,
    cropFocalRegion: true
};

const OrderReturnInitiateTableRowProductImageComponent: React.FC<IOrderReturnInitiateTableRowProps> = (props: IOrderReturnInitiateTableRowProps) => {
    const fallbackImageUrl = getFallbackImageUrl(props.data.returnProduct.simpleProduct.ItemId, props.context.actionContext.requestContext.apiSettings);

    const containerClassName = `${props.className}__product-image-container`;

    return (
        <div className={containerClassName}>
            <Image
                requestContext={props.context.actionContext.requestContext}
                className={`${containerClassName}__product-image`}
                altText={props.data.returnProduct.simpleProduct.Name}
                src={props.data.returnProduct.simpleProduct.PrimaryImageUrl ?? ''}
                fallBackSrc={fallbackImageUrl}
                gridSettings={props.context.request.gridSettings!}
                imageSettings={props.imageSettings ?? defaultImageSettings}
                loadFailureBehavior='empty'
            />
        </div>
    );
};

const _generateQuantityOptions = (className: string, quantity: number | undefined) => {
    const nodes = [];
    let maxQuantity;
    quantity ? maxQuantity = quantity : maxQuantity = 0;

    for (let i = 0; i <= maxQuantity; i++) {
        nodes.push(
            <option className={`${className}__quantity__select-menu__number`} value={i}>
                {i}
            </option>
        );
    }

    return nodes;
};

const _generateQuantitySelectMenu = (props: IOrderReturnInitiateTableRowProps): JSX.Element => {
    const {
        data: { returnProduct, salesLine }
    } = props;
    return (
        <select
            className={`${props.className}__quantity__select-menu`}
            value={returnProduct.itemReturnQuantity}
            onChange={(e) => {
                returnProduct.itemReturnQuantity = +e.target.value;
                props.onChange();
            }}
        >
            {_generateQuantityOptions(props.className, salesLine.Quantity)}
        </select>
    );
};


const OrderReturnInitiateTableCell: React.FC<IOrderReturnInitiateTableRowProps> = (props: IOrderReturnInitiateTableRowProps): JSX.Element => {
    const {
        data: { returnProduct }
    } = props;

    let productUrl = '';
    if (!StringExtensions.isNullOrWhitespace(returnProduct.simpleProduct.Name) && !ObjectExtensions.isNullOrUndefined(returnProduct.simpleProduct.RecordId)) {
        productUrl = getProductPageUrlSync(returnProduct.simpleProduct.Name!, returnProduct.simpleProduct.RecordId, props.context.actionContext);
    }

    return (
        <>
            <td>
                <input
                    className={`${props.className}__checkbox-input`}
                    type="checkbox"
                    onClick={() => {
                        returnProduct.isSelected = !returnProduct.isSelected;
                        props.onChange();
                    }}
                    checked={props.data.returnProduct.isSelected}
                />
            </td>
            <td>
                <OrderReturnInitiateTableRowProductImageComponent {...props} />
            </td>
            <td>
                <div className={`${props.className}__return-product-info`}>
                    <a className={`${props.className}__return-product-name`} href={productUrl}>
                        {returnProduct.simpleProduct.Name}
                    </a>
                    <div className={`${props.className}__return-product-sku`}>
                        {`${props.resources.productSKULabel}${returnProduct.simpleProduct.ItemId}`}
                    </div>
                </div>
            </td>
            <td className={`${props.className}__return-product-quantity`}>
                {_generateQuantitySelectMenu(props)}
            </td>
        </>
    );
};

/**
 * Table row component.
 * @param props - Configuration of the component.
 * @returns React node for the row.
 */
export const OrderReturnInitiateTableRowComponent: React.FC<IOrderReturnInitiateTableRowProps> = (props: IOrderReturnInitiateTableRowProps) => {
    const rowClassName = `${props.className}__row`;

    const headerCells: JSX.Element = <OrderReturnInitiateTableCell {...props} />;

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>{headerCells}</tr>
        </thead>
    );
};
